import { useEffect, useId, useRef, useState } from 'react';
import { Check, Copy, Link } from 'lucide-react';
import { inviteURL } from '../ui-utils';

export function RoomInvite({ code, compact = false }: { code: string; compact?: boolean }) {
  const [copied, setCopied] = useState<'code' | 'link' | null>(null);
  const [showLink, setShowLink] = useState(false);
  const [fallback, setFallback] = useState(false);
  const linkRef = useRef<HTMLInputElement>(null);
  const id = useId();
  const url = inviteURL(code);

  useEffect(() => {
    if (!copied) return;
    const timer = window.setTimeout(() => setCopied(null), 2400);
    return () => window.clearTimeout(timer);
  }, [copied]);

  useEffect(() => {
    if (showLink && fallback) {
      linkRef.current?.focus();
      linkRef.current?.select();
    }
  }, [showLink, fallback]);

  async function copy(kind: 'code' | 'link') {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(kind === 'code' ? code : url);
      setCopied(kind);
      setFallback(false);
    } catch {
      setCopied(null);
      setFallback(true);
      setShowLink(true);
    }
  }

  return (
    <div className={`room-invite${compact ? ' room-invite-compact' : ''}`}>
      <div className="room-code-line">
        <div><span className="eyebrow">Room code</span><strong className="room-code" aria-label={`Room code ${code.split('').join(' ')}`}>{code}</strong></div>
        <button type="button" className="icon-button copy-code" onClick={() => void copy('code')} aria-label={copied === 'code' ? 'Room code copied' : `Copy room code ${code}`} title="Copy room code">
          {copied === 'code' ? <Check size={18} aria-hidden="true" /> : <Copy size={17} aria-hidden="true" />}
        </button>
        {compact && <button type="button" className="icon-button" aria-label="Copy invite link" title="Copy invite link" onClick={() => void copy('link')}><Link size={17} aria-hidden="true" /></button>}
      </div>
      {!compact && <>
        <button type="button" className="button button-secondary invite-button" onClick={() => void copy('link')}>
          {copied === 'link' ? <Check size={17} aria-hidden="true" /> : <Link size={17} aria-hidden="true" />}
          {copied === 'link' ? 'Invite link copied!' : 'Copy invite link'}
        </button>
        <button type="button" className="text-button reveal-link" onClick={() => setShowLink(!showLink)} aria-expanded={showLink} aria-controls={`${id}-link`}>{showLink ? 'Hide invite link' : 'Or share the link yourself'}</button>
      </>}
      {showLink && <div className="invite-fallback" id={`${id}-link`}>
        <label htmlFor={`${id}-url`}>{fallback ? 'Copy this invite link to share' : 'Your invite link'}</label>
        <input ref={linkRef} id={`${id}-url`} value={url} readOnly onFocus={(event) => event.target.select()} aria-label="Invite link, select and copy to share" />
        {compact && <button type="button" className="text-button" onClick={() => setShowLink(false)}>Close link</button>}
      </div>}
      <span className="sr-only" role="status">{copied ? `${copied === 'code' ? 'Room code' : 'Invite link'} copied to clipboard.` : fallback ? 'Clipboard unavailable. The invite link is shown; select and copy it to share.' : ''}</span>
    </div>
  );
}
